import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { RegionProvider } from './context/RegionContext';
import { Navigation } from './components/layout/Navigation';
import { Footer } from './components/layout/Footer';
import { ScrollToTop } from './components/utils/ScrollToTop';
import { Home } from './pages/Home';
import { CookieConsent } from './components/ui/CookieConsent';
import { StructuredData } from './components/seo/StructuredData';
import './styles/globals.css';

import { HelmetProvider } from 'react-helmet-async';

const ServicesOverview = lazy(() =>
  import('./pages/ServicesOverview').then((m) => ({ default: m.ServicesOverview }))
);
const ServiceContentPage = lazy(() =>
  import('./pages/ServiceContentPage').then((m) => ({ default: m.ServiceContentPage }))
);
const IndustriesPage = lazy(() =>
  import('./pages/IndustriesPage').then((m) => ({ default: m.IndustriesPage }))
);
const BranchContentPage = lazy(() =>
  import('./pages/BranchContentPage').then((m) => ({ default: m.BranchContentPage }))
);
const NgoSecurityPage = lazy(() =>
  import('./pages/NgoSecurityPage').then((m) => ({ default: m.NgoSecurityPage }))
);
const EngagementPage = lazy(() => import('./pages/EngagementPage'));
const TrustPage = lazy(() => import('./pages/TrustPage'));
const InsightsIndex = lazy(() => import('./pages/InsightsIndex'));
const InsightsAIGovernance = lazy(() => import('./pages/InsightsAIGovernance'));
const InsightsSapAi = lazy(() => import('./pages/InsightsSapAi'));
const InsightsNis2Gdpr = lazy(() => import('./pages/InsightsNis2Gdpr'));
const InsightsDataQuality = lazy(() => import('./pages/InsightsDataQuality'));
const InsightsCsrdEsg = lazy(() => import('./pages/InsightsCsrdEsg'));
const CompanyPage = lazy(() =>
  import('./pages/CompanyPage').then((m) => ({ default: m.CompanyPage }))
);
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then((m) => ({ default: m.ContactPage }))
);
const ConsultationPage = lazy(() =>
  import('./pages/ConsultationPage').then((m) => ({ default: m.ConsultationPage }))
);
const AssessmentPage = lazy(() => import('./pages/AssessmentPage'));
const LicenseDefensePage = lazy(() =>
  import('./pages/LicenseDefensePage').then((m) => ({ default: m.LicenseDefensePage }))
);
const OfficerShieldPage = lazy(() =>
  import('./pages/OfficerShieldPage').then((m) => ({ default: m.OfficerShieldPage }))
);
const CleanCoreSurgeonPage = lazy(() =>
  import('./pages/CleanCoreSurgeonPage').then((m) => ({ default: m.CleanCoreSurgeonPage }))
);
const MethodologyPage = lazy(() =>
  import('./pages/MethodologyPage').then((m) => ({ default: m.MethodologyPage }))
);
const DatenschutzPage = lazy(() =>
  import('./pages/DatenschutzPage').then((m) => ({ default: m.DatenschutzPage }))
);
const ImpressumPage = lazy(() =>
  import('./pages/ImpressumPage').then((m) => ({ default: m.ImpressumPage }))
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
const CalculatorPage = lazy(() => import('./pages/CalculatorPage'));
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const CfoPage = lazy(() => import('./pages/CfoPage'));
const CisoPage = lazy(() => import('./pages/CisoPage'));
const CioPage = lazy(() => import('./pages/CioPage'));
const FinancialServicesPage = lazy(() => import('./pages/FinancialServicesPage'));
const ManufacturingPage = lazy(() => import('./pages/ManufacturingPage'));
const CriticalInfrastructurePage = lazy(() => import('./pages/CriticalInfrastructurePage'));
const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage'));

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <ThemeProvider>
          <LanguageProvider>
            <RegionProvider>
              <div className="app">
                <a className="skip-link" href="#main-content">Zum Inhalt springen</a>
                <StructuredData />
                <Navigation />
                <main id="main-content">
                  <Suspense fallback={<div style={{ paddingTop: 90 }} />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/services" element={<ServicesOverview />} />
                      <Route path="/services/:serviceId" element={<ServiceContentPage />} />
                      <Route path="/industries" element={<IndustriesPage />} />
                      <Route path="/industries/ngo-security" element={<NgoSecurityPage />} />
                      <Route path="/industries/:branchId" element={<BranchContentPage />} />
                      <Route path="/engagement" element={<EngagementPage />} />
                      <Route path="/trust" element={<TrustPage />} />
                      <Route path="/insights" element={<InsightsIndex />} />
                      <Route path="/insights/ai-governance" element={<InsightsAIGovernance />} />
                      <Route path="/insights/sap-ai-modernization" element={<InsightsSapAi />} />
                      <Route path="/insights/nis2-gdpr-ai" element={<InsightsNis2Gdpr />} />
                      <Route path="/insights/data-quality-ai" element={<InsightsDataQuality />} />
                      <Route path="/insights/csrd-esg-tech" element={<InsightsCsrdEsg />} />
                      <Route path="/company" element={<CompanyPage />} />
                      <Route path="/products/license-defense" element={<LicenseDefensePage />} />
                      <Route path="/products/officer-shield" element={<OfficerShieldPage />} />
                      <Route path="/products/clean-core-surgeon" element={<CleanCoreSurgeonPage />} />
                      <Route path="/methodology" element={<MethodologyPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/consultation" element={<ConsultationPage />} />
                      <Route path="/assessment" element={<AssessmentPage />} />
                      <Route path="/calculator" element={<CalculatorPage />} />
                      <Route path="/partners" element={<PartnersPage />} />
                      <Route path="/resources" element={<ResourcesPage />} />
                      <Route path="/case-studies" element={<CaseStudiesPage />} />
                      <Route path="/for/cfo" element={<CfoPage />} />
                      <Route path="/for/ciso" element={<CisoPage />} />
                      <Route path="/for/cio" element={<CioPage />} />
                      <Route path="/industries/financial-services" element={<FinancialServicesPage />} />
                      <Route path="/industries/manufacturing" element={<ManufacturingPage />} />
                      <Route path="/industries/critical-infrastructure" element={<CriticalInfrastructurePage />} />
                      <Route path="/datenschutz" element={<DatenschutzPage />} />
                      <Route path="/impressum" element={<ImpressumPage />} />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </main>
                <Footer />
                <CookieConsent />
              </div>
            </RegionProvider>
          </LanguageProvider>
        </ThemeProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;
